import React, { useState, useEffect } from "react";
import { showAppAlert } from "../../../store/reducers/appreducer";
import { useDispatch } from "react-redux";
import {
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  alpha,
  useTheme,
  FormHelperText
} from "@mui/material";
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import { updateUser } from "../../../services/userService";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { createGlobalStyle } from 'styled-components';

const GlobalPhoneInputStyles = createGlobalStyle`
  .react-tel-input .country-list {
    width: 300px !important;
  }
  
  .react-tel-input .country-list .country {
    display: flex !important;
    align-items: center !important;
    padding: 10px !important;
  }
  
  .react-tel-input .country-list .country .country-name {
    margin-left: 48px !important;
  }
  
  .react-tel-input .country-list .flag {
    position: absolute !important;
    left: 10px !important;
  }
`;

// EditUserDialog component that includes the dialog
const EditUserDialog = ({ open, onClose, user, updateUserDetails }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phone: user.phone || "",
      });
    }
  }, [user]);

  const validateEmail = (email) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    const trimmedValue = value.trim();

    setFormData({
      ...formData,
      [name]: trimmedValue,
    });

    if (name === "email" && trimmedValue && !validateEmail(trimmedValue)) {
      setErrors({
        ...errors,
        [name]: "Invalid email format",
      });
    } else {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const handlePhoneChange = (value) => {
    // Phone input already includes the "+" prefix
    setFormData({
      ...formData,
      phone: value ? `+${value}` : ""
    });
    
    // Clear any previous phone errors
    setErrors({
      ...errors,
      phone: ""
    });
  };

  const handleSave = async () => {
    const newErrors = {};
  
    const trimmedData = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
    };
  
    if (trimmedData.email && !validateEmail(trimmedData.email)) {
      newErrors.email = "Invalid email format";
    }
    
    if (!trimmedData.phone || !trimmedData.phone.startsWith('+')) {
      newErrors.phone = "Enter Valid Phone Number";
    }
  
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
  
    try {
      const userData = {
        first_name: trimmedData.firstName,
        last_name: trimmedData.lastName,
        email: trimmedData.email,
        phone: trimmedData.phone,
      };
      
      const result = await updateUser(user.userId, userData);
      
      console.log("Updated User Information...", result);
      updateUserDetails({
        firstName: trimmedData.firstName,
        lastName: trimmedData.lastName,
        email: trimmedData.email,
        phone: trimmedData.phone,
      });
      
      dispatch(
        showAppAlert({
          show: true,
          alertType: "success",
          alertMessage: "User information updated successfully",
        })
      );
      
      onClose(); 
    } catch (error) {
      console.error("Failed to update user:", error);
      
      let errorMessage = "Failed to update user. Please try again.";
      
      if (error.response && error.response.data) {
        const apiError = error.response.data;
        

        if (apiError.error && apiError.message) {
          // Handle field-specific errors
          const fieldErrors = {};
          
          // Process each field error from the API response
          Object.entries(apiError.message).forEach(([field, messages]) => {
            fieldErrors[field] = Array.isArray(messages) ? messages[0] : messages;
          });
          
          setErrors(prev => ({
            ...prev,
            ...fieldErrors
          }));
          
          // Create a combined error message 
          const errorMessages = Object.values(apiError.message)
            .map(msgs => Array.isArray(msgs) ? msgs[0] : msgs)
            .join(", ");
          
          errorMessage = errorMessages || errorMessage;
        }
      }
      
      dispatch(
        showAppAlert({
          show: true,
          alertType: "error",
          alertMessage: errorMessage,
        })
      );
    }
  };

  return (
    <>
      <GlobalPhoneInputStyles />
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: theme.shadows[10],
          }
        }}
      >
      <DialogTitle
        sx={{
          m: 0,
          p: 0,
          bgcolor: theme.palette.primary.main,
          color: 'white',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 2,
            px: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PersonOutlineIcon />
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Raleway, sans-serif',
                fontWeight: 600,
                fontSize: '1.2rem',
              }}
            >
              Edit User Profile
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: 'white',
              '&:hover': {
                bgcolor: alpha(theme.palette.common.white, 0.2),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 4, mt: 1 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontFamily: 'Raleway, sans-serif',
                  fontWeight: 500,
                  mb: 1,
                  color: theme.palette.text.primary,
                }}
              >
                First Name*
              </Typography>
              <TextField
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder="Enter First Name"
                variant="outlined"
                fullWidth
                required
                error={!!errors.firstName}
                helperText={errors.firstName}
                InputProps={{
                  sx: {
                    borderRadius: '10px',
                    '&:hover': {
                      boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`
                    },
                    '&.Mui-focused': {
                      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}`
                    }
                  }
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontFamily: 'Raleway, sans-serif',
                  fontWeight: 500,
                  mb: 1,
                  color: theme.palette.text.primary,
                }}
              >
                Last Name*
              </Typography>
              <TextField
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder="Enter Last Name"
                variant="outlined"
                fullWidth
                required
                error={!!errors.lastName}
                helperText={errors.lastName}
                InputProps={{
                  sx: {
                    borderRadius: '10px',
                    '&:hover': {
                      boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`
                    },
                    '&.Mui-focused': {
                      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}`
                    }
                  }
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontFamily: 'Raleway, sans-serif',
                  fontWeight: 500,
                  mb: 1,
                  color: theme.palette.text.primary,
                }}
              >
                Email*
              </Typography>
              <TextField
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter Email"
                variant="outlined"
                fullWidth
                required
                error={!!errors.email}
                helperText={errors.email}
                InputProps={{
                  sx: {
                    borderRadius: '10px',
                    '&:hover': {
                      boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`
                    },
                    '&.Mui-focused': {
                      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}`
                    }
                  }
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontFamily: 'Raleway, sans-serif',
                  fontWeight: 500,
                  mb: 1,
                  color: theme.palette.text.primary,
                }}
              >
                Phone*
              </Typography>
              <Box sx={{ position: 'relative' }}>
                <PhoneInput
                  country={'us'} // Default country
                  value={formData.phone.replace(/^\+/, '')}
                  onChange={handlePhoneChange}
                  inputProps={{
                    name: 'phone',
                    required: true,
                    autoFocus: false,
                  }}
                  inputStyle={{
                    width: '100%',
                    height: '56px',
                    borderRadius: '10px',
                    fontSize: '16px',
                    borderColor: errors.phone ? theme.palette.error.main : undefined,
                  }}
                  buttonStyle={{
                    borderTopLeftRadius: '10px',
                    borderBottomLeftRadius: '10px',
                    borderColor: errors.phone ? theme.palette.error.main : undefined,
                  }}
                  dropdownStyle={{
                    width: '300px',
                  }}
                  containerStyle={{
                    width: '100%',
                  }}
                  placeholder="Enter Phone Number with Country Code"
                />
                {errors.phone && (
                  <FormHelperText error sx={{ ml: 2 }}>
                    {errors.phone}
                  </FormHelperText>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 3 }} />
        
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            mt: 2,
          }}
        >
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{
              width: { xs: '100%', sm: '240px' },
              height: '54px',
              borderRadius: '10px',
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '16px',
              boxShadow: theme.shadows[4],
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: theme.shadows[8],
                transform: 'translateY(-2px)',
              },
            }}
          >
            Save Changes
          </Button>
          <Button
            onClick={onClose}
            variant="outlined"
            startIcon={<CancelIcon />}
            sx={{
              width: { xs: '100%', sm: '240px' },
              height: '54px',
              borderRadius: '10px',
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '16px',
              borderWidth: '2px',
              '&:hover': {
                borderWidth: '2px',
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
          >
            Cancel
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default EditUserDialog;